import { useEffect, useState } from "react";
import { http } from "../api";
import { House, MapPin } from "lucide-react";
import { Modal, PageHeader, ImportExportBar, AddButton, FormRow, EmptyState, PersonLink } from "../shared";

const empty = { family_head: "", address: "", ward: 7, booth: "42", house_type: "Owned", ration_card: "APL", economic_category: "General", member_count: 3, voter_count: 2, religion: "Hindu", caste: "" };

export default function Households() {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [members, setMembers] = useState([]);

  const load = () => http.get("/households").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openDetail = async (h) => {
    setDetail(h);
    const r = await http.get(`/households/${h.household_id}/members`);
    setMembers(r.data);
  };

  const save = async () => {
    setSaving(true);
    try {
      await http.post("/households", { ...form, ward: Number(form.ward), member_count: Number(form.member_count), voter_count: Number(form.voter_count) });
      setShow(false); setForm(empty); load();
    } finally { setSaving(false); }
  };

  return (
    <>
      <PageHeader eyebrow="WARD 7 · FAMILY DATA" title="Households" subtitle={`${rows.length} families with linked members and GPS`}
        actions={<><ImportExportBar entity="households" onImported={load} /><AddButton onClick={() => setShow(true)} label="Add household" testid="households-add-button" /></>} />

      <section className="panel">
        {rows.length === 0 ? <EmptyState icon={House} title="No households" hint="Add a family or import CSV." /> : (
          <div className="grid-cards">
            {rows.map((h, i) => (
              <button key={h.household_id} className="hh-card" data-testid={`household-card-${i}`} onClick={() => openDetail(h)}>
                <div className="hh-head"><House size={18} /><b>{h.family_head}</b></div>
                <p><MapPin size={13} /> {h.address}</p>
                <div className="hh-meta">
                  <span>Ward {h.ward} · Booth {h.booth}</span>
                  <span>{h.member_count} members</span>
                  <span>{h.voter_count} voters</span>
                </div>
                <div className="hh-tags">
                  <span className="tag">{h.house_type}</span>
                  <span className="tag">{h.ration_card}</span>
                  <span className="tag">{h.economic_category}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {show && (
        <Modal title="Add household" onClose={() => setShow(false)} testid="household-modal">
          <div className="form-grid">
            <FormRow label="Family head"><input data-testid="form-head-input" value={form.family_head} onChange={(e) => setForm({ ...form, family_head: e.target.value })} /></FormRow>
            <FormRow label="Booth"><input data-testid="form-booth-input" value={form.booth} onChange={(e) => setForm({ ...form, booth: e.target.value })} /></FormRow>
            <FormRow label="Member count"><input data-testid="form-members-input" type="number" value={form.member_count} onChange={(e) => setForm({ ...form, member_count: e.target.value })} /></FormRow>
            <FormRow label="Voter count"><input data-testid="form-voters-input" type="number" value={form.voter_count} onChange={(e) => setForm({ ...form, voter_count: e.target.value })} /></FormRow>
            <FormRow label="House type">
              <select data-testid="form-house-type" value={form.house_type} onChange={(e) => setForm({ ...form, house_type: e.target.value })}>
                <option>Owned</option><option>Rented</option><option>Ancestral</option>
              </select>
            </FormRow>
            <FormRow label="Ration card">
              <select data-testid="form-ration-card" value={form.ration_card} onChange={(e) => setForm({ ...form, ration_card: e.target.value })}>
                <option>APL</option><option>BPL</option><option>Antodaya</option><option>None</option>
              </select>
            </FormRow>
          </div>
          <FormRow label="Address"><input data-testid="form-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></FormRow>
          <div className="form-actions">
            <button className="outline" data-testid="form-cancel-button" onClick={() => setShow(false)}>Cancel</button>
            <button className="primary" data-testid="form-save-button" disabled={saving || !form.family_head} onClick={save}>{saving ? "Saving…" : "Save"}</button>
          </div>
        </Modal>
      )}

      {detail && (
        <Modal title={`${detail.family_head}'s household`} onClose={() => setDetail(null)} testid="household-detail-modal">
          <p><MapPin size={13} /> {detail.address}</p>
          <div className="hh-meta">
            <span>Ward {detail.ward} · Booth {detail.booth}</span>
            <span>{detail.house_type}</span>
            <span>{detail.ration_card}</span>
          </div>
          <h3 style={{ marginTop: 18, fontSize: 13 }}>Linked members ({members.length})</h3>
          {members.map((m, i) => (
            <div key={m.contact_id} className="member-row" data-testid={`household-member-${i}`}>
              <span className="avatar person-avatar small">{m.name.split(" ").map((x) => x[0]).join("")}</span>
              <div>
                <PersonLink contactId={m.contact_id} name={m.name} testid={`hh-member-link-${m.contact_id}`} className="person-link-name" />
                <small>{m.gender} · {m.age || "—"} · {m.profession || "Resident"}</small>
              </div>
            </div>
          ))}
          {!members.length && <p className="hint">No linked members yet.</p>}
        </Modal>
      )}
    </>
  );
}
